<script lang="ts">
  import NotLoggedInContentWrapper from "../../components/NotLoggedInContentWrapper.svelte";
  import { formatDate } from "$shared/utils/dateTime";
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import {
    Col2Grid,
    Header,
    Page,
    Spacer,
    SpotProduct,
    Toast,
    ValInputField,
    Ver,
    WithResource,
  } from "$components";
  import { DateModel, MyPagesSpotModel, SpotClassModel } from "$models";
  import { setCrumbs } from "$shared/lib/breadcrumbs";
  import { Form, Validate } from "$shared/lib/validation_next";
  import api, { fromApi, mapInto } from "$parkito-api";

  const { params }: { params: { site_id: string } } = $props();

  const siteReq = fromApi(() =>
    api.publicSiteGet({ path: { id: params.site_id } })
  );

  onMount(async () => {
    const site = await $siteReq.load();
    if (site.data) {
      setCrumbs([
        ["Anläggningar", "#/store"],
        [site.data.name, `#/store/${site.data.id}`],
      ]);
    } else {
      Toast.error("Kunde inte hämta anläggning");
    }
  });

  const queueReq = fromApi(() =>
    api.publicSiteGetQueue({ path: { id: params.site_id } })
  );

  onMount(async () => {
    $queueReq.load();
  });

  const defaultDate = formatDate(
    new Date(Math.max(Date.now(), (new Date(2026, 2, 1)).getTime())),
    "YYYY-MM-DD",
  );
  let fieldStartDate = Form.field(
    Validate.required("Startdatum måste anges"),
    defaultDate,
  );

  const spotClasses = fromApi((startDate: string) =>
    api.publicSearchSpotClasses({
      body: {
        siteIds: [params.site_id],
        dateRange: { start: startDate, end: startDate },
      },
    }).then(
      mapInto((scs) =>
        scs.filter((sc) => sc.price.sub > 0).map((sc) =>
          new SpotClassModel(sc)
        )
      ),
    )
  );
  fieldStartDate.subscribe((sd) =>
    $spotClasses.load(sd.res.unwrapOr(defaultDate))
  );
  const spotGroups = fromApi(() =>
    api.publicSearchSpots({
      body: {
        siteIds: [params.site_id],
        pagination: { page: 1, pageSize: 9999 },
      },
    }).then(mapInto((spotGroups) => {
      const spotMap = new Map<string, MyPagesSpotModel>();
      spotGroups.items.forEach((sg) => {
        spotMap.set(
          (sg.tagIds ?? []).sort().join(""),
          new MyPagesSpotModel(sg),
        );
      });
      return spotMap;
    }))
  );

  $spotGroups.load();
  const title = $siteReq.map((s) => `Parkera på ${s.name}`);
</script>

<NotLoggedInContentWrapper>
  <Page backButton asyncTitle={title}>
    {#snippet content()}
      <WithResource
        error="Kunde inte ladda anläggningens kölista"
        api={queueReq}
      >
        {#snippet child(queue)}
          <Ver>
            <Header light size="4">Välj startdatum</Header>
            <ValInputField
              limitWidth
              field={fieldStartDate}
              type="date"
              outset={false}
            />
            <Spacer />
            <Header light size="4">Välj platstyp</Header>
            <WithResource api={spotGroups}>
              {#snippet child(spotGroupMap)}
                <WithResource api={spotClasses}>
                  {#snippet child(spotProducts)}
                    <Col2Grid>
                      {#each spotProducts as spotProduct}
                        <SpotProduct
                          spotGroup={spotGroupMap.get(
                            spotProduct.tags.map((t) => t.id).sort().join(
                              "",
                            ),
                          )}
                          siteId={params.site_id}
                          startDate={new DateModel(
                            $fieldStartDate.res.unwrapOr(defaultDate),
                          )}
                          queueId={queue.id}
                          spotClass={spotProduct}
                        />
                      {/each}
                    </Col2Grid>
                  {/snippet}
                </WithResource>
              {/snippet}
            </WithResource>
          </Ver>
        {/snippet}
      </WithResource>
    {/snippet}
  </Page>
</NotLoggedInContentWrapper>
